import { observer } from "mobx-react";
import Card from "../../../../../components/layouts/card/card";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import FormFieldLineLayout from "../../../../../components/layouts/form-field-line-layout/form-field-line-layout";
import { FormFieldLine } from "../../../../../components/form-field-line/form-field-line";
import { SelectDropdownGrouped } from "../../../../../components/inputs/select-dropdown-grouped/select-dropdown-grouped";
import { SelectDropdown } from "../../../../../components/inputs/select-dropdown/select-dropdown";
import ReturnMethodStore from "../../../stores/return-method-store";
import { IOption } from "../../../../../components/inputs/select-dropdown/select-dropdown";
import CustomAddressFields from "./custom-address-fields/custom-address-fields";
import AddressNote from "./address-note/address-note";
import RestockingLocationsFields from "./restocking-locations-fields/restocking-locations-fields";
import { RETURN_METHOD_TYPE, CREATE_RETURN_IN_INTEGRATION_TYPE } from "../../../return-method-types";
import { NOTE_TYPE, Note } from "../../../../../components/note/note";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import { IValidationGroup } from "../../../../../hooks/validation-group/validation-group-hook";
import Hr from "../../../../../components/hr/hr";
import { SHIPPING_CONNECTION_CODE } from "../../../../../models/IntegrationsTypes";
import { DROP_OFF_LOCATION } from "../../../constants";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import dashboardSettingsStore from "../../../../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES } from "../../../../../models/ProfileCapabilities";

interface Props {
    returnMethod: ReturnMethodStore;
    validationGroup: IValidationGroup;
}


const AddressCard: React.FC<Props> = observer(({returnMethod, validationGroup} :Props) => {
    const { getTranslation } = useDashboardTranslation();
    const scope = 'dashboard-return-method-editor';

    const shipWithAnyCarrierCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.RETURN_METHOD_SHIP_WITH_ANY_CARRIER);
    const returnToDropOffLocationCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.RETURN_METHOD_RETURN_TO_DROP_OFF_LOCATION);
    const isShipWithAnyCarrier = returnMethod?.methodType === RETURN_METHOD_TYPE.SHIP_WITH_ANY_CARRIER;
    const isDropOffLocation = returnMethod?.methodType === RETURN_METHOD_TYPE.DROP_OFF_LOCATION;

    const shipWithPrepaidLabelCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.RETURN_METHOD_SHIP_WITH_PREPAID_LABEL);
    const isShipWithPrepaidLabel = returnMethod?.methodType === RETURN_METHOD_TYPE.SHIP_WITH_PRE_PAID_LABEL;

    const returnAddressesList = returnMethod.getReturnAdressesList(getTranslation);
    const selectedReturnAddress = () => {
        let locationId = returnMethod.returnAddress?.locationId;
        if (!locationId) {
            locationId = returnMethod.useDynamicAddress
                ? DROP_OFF_LOCATION.DYNAMIC_ADDRESS
                : DROP_OFF_LOCATION.CUSTOM_ADRESS;
        }
        return returnAddressesList.map(category => category.options)
            .flat()
            .find(option => option.value === locationId);
    }


    return(
        <Card
            toolbar={{
                title: <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getTranslation("ADDRESS_CARD_TITLE", scope)}</FontBody1>,
            }}
            isExpanded={false}
        >
            { returnMethod.isAddressConfigurable() ?
                <>
                    { returnMethod.isShipWithAnyCarrierSelected() &&
                        <Note
                            type={NOTE_TYPE.INFORMATION}
                            body={
                                <FontBody2>
                                    { returnMethod.isAddressOrPackingSlipEnabled()
                                        ? getTranslation("RETURN_ADDRESS_AND_RESTOCKING_LOCATION_NOTE", scope)
                                        : getTranslation("RETURN_ADDRESS_AND_RESTOCKING_LOCATION_NOTE_2", scope)
                                    }
                                </FontBody2>
                            }
                        />
                    }
                    { returnMethod.isDropOffWithIntegration() &&
                            <FormFieldLineLayout>
                                <Note type={NOTE_TYPE.INFORMATION} body={<FontBody2>{getTranslation("DROP_OFF_NOTE", scope)}</FontBody2>} />
                            </FormFieldLineLayout>
                    }
                    { returnMethod.showReturnAddressSection()
                        && (!isDropOffLocation || returnToDropOffLocationCapability.isReturnAddressSupported)
                        && (!isShipWithPrepaidLabel || shipWithPrepaidLabelCapability.isReturnAddressSupported) &&
                        <>
                            <FormFieldLineLayout>
                                { returnMethod.aggregatorCode === SHIPPING_CONNECTION_CODE.USPS &&
                                    <Note type={NOTE_TYPE.INFORMATION} body={<FontBody2>{getTranslation("RETURN_ADDRESS_USPS_NOTE", scope)}</FontBody2>} />
                                }
                                <FormFieldLine
                                    label={getTranslation("RETURN_ADDRESS", scope)}
                                    note={getTranslation("RETURN_ADDRESS_NOTE", scope)}
                                    required={true}
                                >
                                    <SelectDropdownGrouped
                                        options={returnAddressesList}
                                        value={selectedReturnAddress()}
                                        onChange={(v) => returnMethod.setSelectedReturnAddress(v as IOption)}
                                        required
                                        validationGroup={validationGroup}
                                    />
                                </FormFieldLine>
                            </FormFieldLineLayout>

                            {!!returnMethod.showCustomAddressFields() &&
                                <CustomAddressFields returnMethod={returnMethod} validationGroup={validationGroup}/>
                            }

                            {!!returnMethod.showCustomAddressNote() &&
                                <FormFieldLine label="">
                                    <Note type={NOTE_TYPE.INFORMATION} body={<FontBody2>{getTranslation("CUSTOM_ADDRESS_NOTE_2", scope)}</FontBody2>} />
                                </FormFieldLine>
                            }

                            {!!returnMethod.returnAddress?.locationId &&
                                <AddressNote address={returnMethod.getLocation(returnMethod.returnAddress?.locationId as string)}/>
                            }
                        </>
                    }
                </>
                :
                <Note
                    type={NOTE_TYPE.INFORMATION}
                    body={
                        <FontBody2>{returnMethod.getAddressReasonNote()}</FontBody2>
                    }
                />
            }
            <Hr />
            {(!isShipWithAnyCarrier || shipWithAnyCarrierCapability.isDefaultRestockingLocationSupported) && (!isShipWithPrepaidLabel || shipWithPrepaidLabelCapability.isDefaultRestockingLocationSupported) &&
            (!isDropOffLocation || returnToDropOffLocationCapability.isDefaultRestockingLocationSupported) && (
                <RestockingLocationsFields returnMethod={returnMethod} validationGroup={validationGroup} />
            )}
            {returnMethod.hasInventoryTrackingConnections && returnMethod.methodType !== RETURN_METHOD_TYPE.NO_SHIPMENT_REQUIRED &&
                    <FormFieldLine
                        label={getTranslation("UPDATE_WMS_3PL_ERP", scope)}
                        required={true}
                    >
                        <SelectDropdown
                            value={returnMethod?.getSelectedCreateReturnInWmsOption(getTranslation)}
                            options={returnMethod?.getCreateReturnInWmsOptionsList(getTranslation) ?? []}
                            onChange={v => returnMethod?.setCreateReturnInWms(v?.value as CREATE_RETURN_IN_INTEGRATION_TYPE | number)}
                            required
                            validationGroup={validationGroup}
                        />
                    </FormFieldLine>
                }
        </Card>
    )
});

export default AddressCard;